/**
 * Messages on this device (module 11).
 *
 * A project member's message is written here first — the local copy and the
 * outbox entry in one transaction — and sent by the sync engine, in order, as
 * soon as there is a connection. Photos and documents go through the media
 * queue and upload once their message has reached the server. Conversations
 * and the last 30 days of their messages (at most 200 per conversation) are
 * pulled so the latest threads read with no signal.
 *
 * Append-only: a message is never merged. An edit is a separate `update`
 * that the server accepts only within 15 minutes of receiving the message.
 */
import type { Message } from '@/lib/api/messaging-types';
import { uuidv7 } from '@/lib/id';
import { captureMedia } from '@/lib/media/uploader';

import { db, type ConversationRecord, type MessageRecord, type OutboxRecord } from './db';
import { queueFieldMutation } from './field';

export const MESSAGE_ENTITY = 'message';
export const KEEP_PER_CONVERSATION = 200;
export const KEEP_DAYS = 30;

export type Author = { id: string; name: string; organization: string };

export type DraftMessage = {
  body: string;
  replyTo?: { id: string; author_name: string; excerpt: string } | null;
  mentions?: string[];
  files?: File[];
};

/** When a message happened, for ordering: server receive time, else the device's. */
export function sortKey(message: Pick<Message, 'created_at' | 'created_at_device'>): string {
  return message.created_at ?? message.created_at_device ?? '';
}

export function byTime(a: Message, b: Message): number {
  return sortKey(a).localeCompare(sortKey(b)) || a.id.localeCompare(b.id);
}

/** Write a message (offline-capable). Returns its id. */
export async function queueMessage(
  projectId: string,
  conversationId: string,
  author: Author,
  draft: DraftMessage,
): Promise<string> {
  const id = uuidv7();
  const files = draft.files ?? [];
  await queueFieldMutation(MESSAGE_ENTITY, {
    entity_id: id,
    project_id: projectId,
    operation: 'create',
    payload: {
      conversation_id: conversationId,
      body: draft.body,
      reply_to_id: draft.replyTo?.id ?? null,
      // Shown on this device until the server's copy arrives (the server reads ids only).
      reply_to: draft.replyTo ? { ...draft.replyTo, removed: false } : null,
      mentions: draft.mentions ?? [],
      attachment_count: files.length,
      created_at_device: new Date().toISOString(),
      author: {
        id: author.id,
        name: author.name,
        organization: author.organization,
        kind: 'member',
      },
    },
    summary: draft.body.slice(0, 60) || 'Photo',
  });
  for (const file of files) {
    // No location on a chat photo: where someone was is not the conversation's business.
    await captureMedia({ projectId, entityType: MESSAGE_ENTITY, entityId: id }, file, {
      locate: false,
    });
  }
  return id;
}

/** Correct one's message (sent when there is a connection; refused after 15 minutes). */
export async function queueEdit(record: MessageRecord, body: string): Promise<void> {
  await queueFieldMutation(MESSAGE_ENTITY, {
    entity_id: record.id,
    project_id: record.project_id,
    operation: 'update',
    payload: { changes: { body } },
    summary: body.slice(0, 60),
  });
}

export async function listLocalMessages(conversationId: string): Promise<MessageRecord[]> {
  const rows = await db.messages.where('data.conversation_id').equals(conversationId).toArray();
  return rows.sort((a, b) => byTime(a.data, b.data));
}

export async function listLocalConversations(projectId?: string): Promise<ConversationRecord[]> {
  const rows = projectId
    ? await db.conversations.where('project_id').equals(projectId).toArray()
    : await db.conversations.toArray();
  return rows.sort((a, b) =>
    (b.data.last_message_at ?? '').localeCompare(a.data.last_message_at ?? ''),
  );
}

/** Outbox rows for this conversation's messages (pending, sending, failed…). */
export async function messageOutbox(conversationId: string): Promise<OutboxRecord[]> {
  return db.outbox
    .filter(
      (row) =>
        row.entity_type === MESSAGE_ENTITY &&
        row.state !== 'applied' &&
        row.payload['conversation_id'] === conversationId,
    )
    .toArray();
}

/**
 * Keep the device bounded: per conversation, the latest 200 messages of the
 * last 30 days. Messages still waiting to be sent are never dropped.
 */
export async function pruneMessages(projectId: string, now = Date.now()): Promise<number> {
  const cutoff = new Date(now - KEEP_DAYS * 24 * 3600 * 1000).toISOString();
  const rows = await db.messages.where('project_id').equals(projectId).toArray();
  const groups = new Map<string, MessageRecord[]>();
  for (const row of rows) {
    const list = groups.get(row.data.conversation_id) ?? [];
    list.push(row);
    groups.set(row.data.conversation_id, list);
  }
  const drop: string[] = [];
  for (const list of groups.values()) {
    list.sort((a, b) => byTime(b.data, a.data));
    list.forEach((row, index) => {
      if (row.local_state === 'pending') return;
      if (index >= KEEP_PER_CONVERSATION || sortKey(row.data) < cutoff) drop.push(row.id);
    });
  }
  if (drop.length) await db.messages.bulkDelete(drop);
  return drop.length;
}

/** Remember the server's copy of a message we just read online (deduplicates with pull). */
export function newMessageId(): string {
  return uuidv7();
}
